import { useAppDispatch } from 'hooks';
import { useEffect } from 'react';
import { addScreenReaderNotification } from 'reducers/workflows';

/**
 * Hook to enable keyboard navigation through messages using ArrowUp and ArrowDown keys.
 * Works only when a message is focused.
 */
export default function useMessageKeyboardNavigation(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      // Bail early if the focused element is not a message:
      if (
        !document.activeElement?.matches(
          '.js-bot-message, .js-user-message-focusable',
        )
      ) {
        return;
      }

      const focusableMessages = document.querySelectorAll<HTMLElement>(
        '.js-bot-message, .js-user-message-focusable',
      );

      const activeElementIndex = Array.from(focusableMessages).indexOf(
        document.activeElement as HTMLElement,
      );

      const nextMesssage = focusableMessages[activeElementIndex + 1];
      const previousMessage = focusableMessages[activeElementIndex - 1];

      if (event.key === 'ArrowDown') {
        if (nextMesssage) {
          event.preventDefault();
          return nextMesssage.focus();
        }

        dispatch(addScreenReaderNotification('No more messages below.'));
      }

      if (event.key === 'ArrowUp') {
        if (previousMessage) {
          event.preventDefault();
          return previousMessage.focus();
        }

        dispatch(addScreenReaderNotification('No more messages above.'));
      }
    };

    // `document.body` can be `null`
    document.body?.addEventListener('keydown', listener);

    return () => {
      // `document.body` can be `null`
      document.body?.removeEventListener('keydown', listener);
    };
  }, [dispatch]);
}
